import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import React from 'react';
import { useTranslation } from 'react-i18next';
import Flex, { Box } from 'components/Flex';
import Button from 'components/Button';
import FormField, { DefaultFormFieldLayout } from 'components/FormField';
import FormStack from 'components/FormStack';
import ShadedCard from 'components/ShadedCard';
import Panel from 'components/Panel';
import Switch from 'components/Switch';
import { Option, Select } from 'components/Select';
import CheckboxOption from 'components/CheckboxOption';
import Heading from 'components/Heading';
import useUniqueId from 'hooks/useUniqueId';
import useModalQueryParam from 'hooks/useModalQueryParam';
import DisconnectConfirmationModal from './DisconnectConfirmationModal';
import { useAutoJoinForm } from './hooks/useAutoJoinForm';
import { useUpdateMyCalendarAutoJoinSettings } from './hooks/useUpdateMyCalendarAutoJoinSettings';
import CalendarStatus from './CalendarStatus';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
function CalendarLinkedContent(_ref) {
  var settings = _ref.settings,
    onClose = _ref.onClose;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var hasOnClose = typeof onClose === 'function';
  var _useModalQueryParam = useModalQueryParam('disconnect-calendar'),
    _useModalQueryParam2 = _slicedToArray(_useModalQueryParam, 2),
    hasDisconnectConfirmationModal = _useModalQueryParam2[0],
    toggleDisconnectConfirmationModal = _useModalQueryParam2[1];
  var autoJoinForm = useAutoJoinForm({
    isAutoJoinEnabled: settings.isAutoJoinEnabled,
    autoJoinSettings: settings.autoJoinSettings
  });
  var fields = autoJoinForm.fields,
    hasErrors = autoJoinForm.hasErrors,
    wasChanged = autoJoinForm.wasChanged,
    reset = autoJoinForm.reset;
  var _useUpdateMyCalendarA = useUpdateMyCalendarAutoJoinSettings({
      onSuccess: reset
    }),
    updateMyCalendarAutoJoinSettings = _useUpdateMyCalendarA.updateMyCalendarAutoJoinSettings,
    isUpdateLoading = _useUpdateMyCalendarA.isLoading;
  function onSubmit(_x) {
    return _onSubmit.apply(this, arguments);
  }
  function _onSubmit() {
    _onSubmit = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee(e) {
      var updatedSettings;
      return _regeneratorRuntime.wrap(function (_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            e.preventDefault();

            // wasChanged can be false when hasOnClose, since the button must also
            // work as a plain close action - skip the no-op update request.
            if (!wasChanged) {
              _context.next = 1;
              break;
            }
            updatedSettings = {
              isAutoJoinEnabled: fields.isAutoJoinEnabled.value,
              autoJoinSettings: {
                meeting_type: fields.meetingType.value,
                only_meetings_I_organise: fields.onlyMeetingsIOrganise.value
              }
            };
            _context.next = 1;
            return updateMyCalendarAutoJoinSettings(updatedSettings);
          case 1:
            onClose === null || onClose === void 0 || onClose();
          case 2:
          case "end":
            return _context.stop();
        }
      }, _callee);
    }));
    return _onSubmit.apply(this, arguments);
  }
  var enabledFieldId = useUniqueId();
  var meetingTypeFieldId = useUniqueId();
  var provider = settings.provider;
  var hasActiveSubscription = settings.hasActiveSubscription;
  var isGoogleCalendar = provider === 'google';
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(Box, {
      mb: "l",
      children: /*#__PURE__*/_jsxs(Flex, {
        justifyContent: "space-between",
        gap: "s",
        mb: "xxs",
        children: [/*#__PURE__*/_jsx(CalendarStatus, {
          translationKey: isGoogleCalendar ? 'state_google_calendar_connected' : 'state_microsoft_calendar_connected'
        }), /*#__PURE__*/_jsx(Button, {
          size: "medium",
          onClick: toggleDisconnectConfirmationModal,
          children: t('actions_disconnect')
        })]
      })
    }), /*#__PURE__*/_jsxs("form", {
      onSubmit: onSubmit,
      children: [/*#__PURE__*/_jsxs(ShadedCard, {
        p: "s",
        mb: "m",
        children: [/*#__PURE__*/_jsx(Heading, {
          as: "h3",
          mb: "m",
          size: "small",
          children: t('auto_join_settings_form_title')
        }), /*#__PURE__*/_jsx(DefaultFormFieldLayout, {
          horizontal: true,
          children: /*#__PURE__*/_jsxs(FormStack, {
            children: [/*#__PURE__*/_jsx(FormField, {
              required: true,
              label: t('state_enabled'),
              id: enabledFieldId,
              children: /*#__PURE__*/_jsx(Switch, {
                id: enabledFieldId,
                onChange: function onChange() {
                  return fields.isAutoJoinEnabled.set(!fields.isAutoJoinEnabled.value);
                },
                checked: fields.isAutoJoinEnabled.value,
                disabled: !hasActiveSubscription
              })
            }), /*#__PURE__*/_jsx(FormField, {
              required: true,
              as: "span",
              label: t('auto_join_settings_form_meetings_title'),
              labelId: meetingTypeFieldId,
              children: /*#__PURE__*/_jsxs(FormStack, {
                children: [/*#__PURE__*/_jsxs(Select, {
                  labelledById: meetingTypeFieldId,
                  size: "medium",
                  menuPlacement: "bottom",
                  value: fields.meetingType.value || 'all',
                  onChange: function onChange(value) {
                    return fields.meetingType.set(value);
                  },
                  isDisabled: !hasActiveSubscription,
                  children: [/*#__PURE__*/_jsx(Option, {
                    value: "all",
                    children: t('auto_join_settings_form_meetings_all')
                  }), /*#__PURE__*/_jsx(Option, {
                    value: "external",
                    children: t('auto_join_settings_form_meetings_only_external')
                  }), /*#__PURE__*/_jsx(Option, {
                    value: "internal",
                    children: t('auto_join_settings_form_meetings_only_internal')
                  })]
                }), /*#__PURE__*/_jsx(CheckboxOption, {
                  label: t('auto_join_settings_form_meetings_only_organised_by_me'),
                  disabled: !hasActiveSubscription,
                  checked: !!fields.onlyMeetingsIOrganise.value,
                  onChange: function onChange() {
                    return fields.onlyMeetingsIOrganise.set(!fields.onlyMeetingsIOrganise.value);
                  }
                })]
              })
            })]
          })
        })]
      }), /*#__PURE__*/_jsx(Panel, {
        align: "center",
        flush: true,
        children: /*#__PURE__*/_jsx(Button, {
          type: "submit",
          icon: "ok",
          variant: "primary",
          isDisabled: hasOnClose ? hasErrors : hasErrors || !wasChanged || !hasActiveSubscription,
          isLoading: isUpdateLoading,
          children: t(hasOnClose ? 'actions_save_and_close' : 'actions_save')
        })
      })]
    }), hasDisconnectConfirmationModal && /*#__PURE__*/_jsx(DisconnectConfirmationModal, {
      onClose: toggleDisconnectConfirmationModal,
      provider: provider
    })]
  });
}
export default CalendarLinkedContent;